import { useDispatch, useSelector } from 'react-redux';
import { useCallback } from 'react';
import useAxiosPrivate from './useAxiosPrivate';
import useAuth from './useAuth';
import {
  setLocations,
  setLocationLoading,
  setLocationSwitching,
  setLocationError,
  clearLocationError,
  setCurrentLocationId,
  setCurrentLocation,
} from '../redux/locationSlice';
import { useAppSelector } from '../redux/helper';
import { RootState } from '../redux/types';
import { getEmployeeLocations, switchUserLocation } from '../api/locations';
import { addInAppBusinessInfo, addInAppEmployeeInfo } from '../redux/AuthSlice';
import { clearFetchedMpesaConfig as clearRootMpesaConfig } from '../redux/mpesaConfig';
import { clearFetchedMpesaConfig as clearBackofficeMpesaConfig } from '../redux/backoffice/mpesaConfigSlice';
import { resetCompanyProfile } from '../redux/backoffice/companyProfileSlice';
import { clearSuppliersState } from '../redux/backoffice/suppliersSlice';
import { clearAllCustomerOrders } from '../redux/backoffice/customerOrdersSlice';
import { clearCustomersState } from '../redux/backoffice/customersSlice';
import { clearCloudCustomerSummaries } from '../redux/OrdersSlice';
import { isEmployeeAdminRole } from '../utils/roles';
import { dispatchOwnerActivationEvent } from '../services/ownerActivationEvents';
import appAnalytics from '../services/appAnalytics';
import { buildAppAnalyticsIdentify } from '../services/appAnalyticsIdentify';

const useLocation = () => {
  const dispatch = useDispatch();
  const axiosPrivate = useAxiosPrivate();
  const { auth } = useAuth();
  const employeedata = useAppSelector(
    (state: RootState) => state.auth.inApp.account.employeeInfo,
  );

  const locations = useSelector((state: RootState) => state.location.locations);
  const currentLocation = useSelector(
    (state: RootState) => state.location.currentLocation,
  );
  const isLoading = useSelector((state: RootState) => state.location.isLoading);
  const isLocationSwitching = useSelector(
    (state: RootState) => state.location.isLocationSwitching,
  );
  const error = useSelector((state: RootState) => state.location.error);
  const fetchLocations = useCallback(async () => {
    if (!auth?.accessToken || !employeedata?._id) return;

    try {
      dispatch(setLocationLoading(true));
      dispatch(clearLocationError());
      console.log('fetching locations...', employeedata._id, auth);

      const response = await getEmployeeLocations(
        axiosPrivate,
        employeedata._id,
        auth,
      );
      console.log('locations fetched', response.data);

      if (response.data.success || response.data.status === 200) {
        const locationsData = response.data.data.allowed_locations;
        //changed from default_location to current_location so that user logs to assigned location
        const currentLocationData = response.data.data.current_location;
        dispatch(setLocations(locationsData));
        dispatch(setCurrentLocation(currentLocationData));
        dispatch(setCurrentLocationId(currentLocationData._id));
      } else {
        dispatch(
          setLocationError(
            response.data.message || 'Failed to fetch locations',
          ),
        );
      }
    } catch (error: any) {
      console.error('Error fetching locations:', error);
      dispatch(setLocationError(error.message || 'Failed to fetch locations'));
    } finally {
      dispatch(setLocationLoading(false));
    }
  }, [
    axiosPrivate,
    auth?.accessToken,
    employeedata?._id,
    dispatch,
    currentLocation,
  ]);

  const switchLocation = useCallback(
    async (locationId: string) => {
      if (!auth?.accessToken || !locationId) return;

      try {
        dispatch(setLocationSwitching(true));
        dispatch(clearLocationError());

        // Find the location in the current locations array
        const selectedLocation = locations.find(
          (loc: any) =>
            loc._id === locationId || loc.bp_location_id === locationId,
        );
        if (!selectedLocation) {
          throw new Error('Location not found');
        }

        const switchLocationResponse = await switchUserLocation(
          axiosPrivate,
          employeedata._id,
          locationId,
          employeedata.bp_employee_role?.toLowerCase(),
          auth,
        );
        const locationData = switchLocationResponse.data.data.current_location;
        const businessInformation =
          switchLocationResponse.data.data.businessInformation;
        // Update current location in Redux - store the location ID
        const locationIdToStore = locationData._id;
        dispatch(setCurrentLocationId(locationIdToStore));
        dispatch(setCurrentLocation(locationData));
        dispatch(
          addInAppEmployeeInfo({ bp_employee_current_location: locationId }),
        );

        // Wipe persisted location-scoped caches that neither picker clears.
        // Without this the previous location's data survives the switch:
        //   - mpesaConfig (root) → wrong "Connect M-PESA" nudge state on Home
        //   - backofficeMpesaConfig.fetched → old shortcode/keys shown on the integration screen
        //   - backofficeCompanyProfile → old shop name/address/currency on Company Profile until refetch
        //   - backofficeSuppliers → old suppliers / POs / goods receipts leaking cross-location
        // Draft is intentionally NOT reset — it's the user's in-progress form input,
        // not a cache. The integration screens own its lifecycle.
        // Cleared after the API succeeds so a failed switch keeps the current caches.
        dispatch(clearRootMpesaConfig());
        dispatch(clearBackofficeMpesaConfig());
        dispatch(resetCompanyProfile());
        dispatch(clearSuppliersState());
        dispatch(clearAllCustomerOrders());
        dispatch(clearCustomersState());
        dispatch(clearCloudCustomerSummaries());
        dispatch(
          addInAppBusinessInfo({
            name: businessInformation.bp_user_business_name,
            description: businessInformation?.bp_user_business_description,
            Adrress: businessInformation.bp_user_business_address,
            type: businessInformation?.bp_user_business_type,
          }),
        );

        // Activation: fire once after confirmed service success. Completes
        // visit_test_location only when the destination is a test location.
        // LocationPicker keeps its own safety alert; do not dispatch there.
        const destinationId = String(
          locationData?._id || locationIdToStore || '',
        );
        if (destinationId) {
          dispatchOwnerActivationEvent(dispatch, {
            type: 'LOCATION_SWITCHED',
            occurredAt: new Date().toISOString(),
            source: 'location_picker',
            locationId: destinationId,
            locationType:
              locationData?.bp_location_is_system_generated === true
                ? 'test'
                : 'real',
          });
        }

        try {
          const locationType =
            locationData?.bp_location_is_system_generated === true
              ? 'test'
              : 'real';
          const identify = buildAppAnalyticsIdentify({
            employee: employeedata,
            location: {
              ...locationData,
              bp_location_type: locationType,
            },
            business: {
              name: businessInformation?.bp_user_business_name,
            },
          });
          if (identify) appAnalytics.identify(identify);
        } catch (e) {
          console.log('app analytics location identify skipped', e);
        }

        return switchLocationResponse;
      } catch (error: any) {
        dispatch(setLocationSwitching(false));
        console.error('Error switching location:', error);
        dispatch(
          setLocationError(error.message || 'Failed to switch location'),
        );
        throw error;
      } finally {
        dispatch(setLocationSwitching(false));
      }
    },
    [
      axiosPrivate,
      auth?.accessToken,
      locations,
      employeedata,
      employeedata?._id,
      employeedata?.bp_employee_role,
    ],
  );

  const getCurrentLocationId = useCallback(() => {
    return currentLocation || null;
  }, [currentLocation]);

  return {
    locations,
    currentLocation,
    isLoading,
    isLocationSwitching,
    error,
    fetchLocations,
    switchLocation,
    getCurrentLocationId,
    isAdmin: isEmployeeAdminRole(employeedata?.bp_employee_role),
  };
};

export default useLocation;
